import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Grid from '@precis-digital/kurama/Grid';
import MaximizeIcon from '@precis-digital/kurama/icons/MaximizeIcon';
import MinimizeIcon from '@precis-digital/kurama/icons/MinimizeIcon';
import MultiSelectDropdown, { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import Skeleton from '@precis-digital/kurama/Skeleton';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { TableContainer } from '@precis-digital/kurama/Table';
import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';
import { makeErrorToast } from 'shared/components/Toaster';
import { useAuth } from 'shared/context/AuthContext';
import { downloadCSV, exportCsvToGoogleSheets } from 'shared/utils/csvExport';
import SearchButton from '../../SearchButton';
import { CHART_CARD_CARD_LAYOUT, CHART_CARD_TABLE_LAYOUT } from '../constants';
import { usePrepareTotalsAndRangeData } from '../hooks';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
import usePrepareData from '../hooks/usePrepareData';
import { getActionClickHandler, getTableActionContext, resolveServerFetchTotalsAndRange } from './table/utils';
import type { ChangeCardType, ChartCardChartContainerProps, ChartCardTableColumnsChoice, ChartCardTableContainerProps, ChartCardTableContainerSizeProps } from '../internalTypes';
import type { ChartCardActionContext, ChartCardType, ChartDataRow } from '../types';
import { getChartCardTableContainerSize } from '../utils/card';
import { buildColumnsSelectionOptions } from '../utils/columnsSelection';
import { errorLocal } from '../utils/generics';
import { buildTableCsvContent } from '../utils/table';
import ChartCardMoreMenu from './ChartCardMoreMenu';
import IconTemplates from './IconTemplates';
import ChartTableSwap from './styled/ChartTableSwap';
import StyledCard from './styled/StyledCard';
import StyledTypographyNoWrap from './styled/StyledTypographyNoWrap';
import { Spacer } from './table/styled';
import Table from './table/Table';
import { useElementClientWidth, useTableBodyScrollDecoration } from './table/hooks';
import type { ChartCardTable } from './table/tanstack';
import type { CompleteTablePaginationState } from './table/types';

/**
 * This component is used to contain the chart, and all the UI elements external to the chart:
 * * The title
 * * The actions
 * * The expanded toggle
 * * The more menu actions
 * * The variable / displayed choices (dimensions, metrics, etc.)
 */
const ChartCardTableContainer = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: ChartCardTableContainerProps<DataKey, DataRow>): ReactElement => {
  const {
    view,
    cardSize,
    tableSize,
    title,
    downloadTitle,
    actions,
    moreMenuActions,
    expandedToggle,
    allowResetCard,
    allowExport,
    allowToggleUsePagination,
    toggleUsePagination,
    useFuzzySearch,
    usePagination,
    useDefaultSorting,
    useDefaultPinning,
    useColumnsDecorations,
    useBodyScrollDecoration,
    useOverscrollContain,
    allowRowsSelection,
    allowMultipleColumnsSorting,
    allowColumnsSelection,
    allowColumnFiltering,
    allowDateFiltering,
    allowColumnResizing,
    allowColumnAutoResizing,
    allowColumnPinning,
    allowColumnReordering,
    enableNullDimensionValue,
    isServerFetch,
    onOpenColumnFilter,
    columns,
    elementsDescriptionsMap,
    state,
    dispatch,
    otherCardTypes,
    changeCardType,
    serverTotalRows,
    serverTotals,
    serverPagination,
    serverSorting,
    onServerPaginationChange,
    onServerSortingChange,
    isFetchLoading,
    t
  } = props;
  const {
    choicesList,
    choicesMap,
    selections,
    selectionsUpdated
  } = state;
  const theme = useTheme();
  const {
    processedData,
    newMetricsMap
    // transposedMetricsLists
  } = usePrepareData<DataKey, DataRow>(props);
  const totalsAndRangeDataFromRows = usePrepareTotalsAndRangeData<DataKey, DataRow>({
    data: processedData,
    metricsMap: newMetricsMap,
    columns
  });
  const totalsAndRangeData = useMemo(() => resolveServerFetchTotalsAndRange<DataKey, DataRow>({
    isServerFetch,
    serverTotals,
    totalsAndRangeDataFromRows,
    columns,
    metricsMap: newMetricsMap
  }), [serverTotals, totalsAndRangeDataFromRows, columns, newMetricsMap, isServerFetch]);
  const defaults = useChartCardDefaults();
  const {
    setRef: setTableContainerRef,
    ref: tableContainerRef,
    clientWidth: containerClientWidth
  } = useElementClientWidth();
  const tableHeadRootRef = useRef<HTMLTableSectionElement>(null);
  const tableFooterRootRef = useRef<HTMLTableSectionElement>(null);
  const tableRef = useRef<ChartCardTable<DataKey, DataRow> | null>(null);
  const resetTableRef = useRef<(() => void) | null>(null);
  const getContext = useCallback(() => getTableActionContext(tableRef.current), [tableRef]);
  const [completePaginationState, setCompletePaginationState] = useState<CompleteTablePaginationState | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const handleSearchSubmit = (value: string): void => {
    setGlobalFilter(value);
  };
  const {
    currentUser
  } = useAuth();
  const handleExport = (target: 'csv' | 'googleSheets'): void => {
    const downloadFileName = downloadTitle ?? title;
    const csvContent = buildTableCsvContent({
      columns,
      data: processedData
    });
    if (csvContent.length === 0) {
      makeErrorToast(t('nothingToExport'));
      return;
    }
    if (target === 'googleSheets') {
      try {
        exportCsvToGoogleSheets({
          csvContent,
          title: downloadFileName,
          authuser: currentUser?.email != null && currentUser.email !== '' ? currentUser.email : undefined
        });
      } catch (error) {
        errorLocal('Failed to export table to Google Sheets', error);
        makeErrorToast(t('failedToExportGoogleSheets'));
      }
      return;
    }
    downloadCSV(csvContent, `${downloadFileName}.csv`);
  };
  const handleResetCard = (): void => {
    if (selectionsUpdated) {
      dispatch({
        type: 'resetSelections'
      });
    }
    resetTableRef.current?.();
  };
  const moreMenu: ReactElement | null = <ChartCardMoreMenu<DataKey, DataRow> view={view} moreMenuActions={moreMenuActions} expandedToggle={expandedToggle} handleExport={handleExport} needsResetCard={selectionsUpdated || resetTableRef.current != null} allowResetCard={allowResetCard} handleResetCard={handleResetCard} allowExport={allowExport} cardType="table" otherCardTypes={otherCardTypes} changeCardType={changeCardType} usePagination={usePagination} allowToggleUsePagination={allowToggleUsePagination} toggleUsePagination={toggleUsePagination} getContext={getContext} t={t} />;
  const buttons = getButtons({
    tableRef,
    getContext,
    otherCardTypes,
    changeCardType,
    actions,
    expandedToggle
  });
  const selectedColumnsIds = selections['columns' as DataKey] as DataKey[];
  const onChange = (_: string | null, items: MultiSelectDropdownItem[]): void => {
    const columnsIds = items.map(({
      value: value_0
    }) => value_0);
    dispatch({
      type: 'multipleSet',
      id: 'columns' as DataKey,
      items: columnsIds,
      others: false
    });
  };
  const columnsOptions = useMemo(() => {
    const options = (choicesMap['columns' as DataKey] as ChartCardTableColumnsChoice<DataKey, DataRow>).change.options;
    return buildColumnsSelectionOptions({
      options,
      selectedColumnsIds,
      elementsDescriptionsMap,
      useColumnsDecorations,
      t
    });
  }, [choicesMap, elementsDescriptionsMap, useColumnsDecorations, selectedColumnsIds, t]);
  const {
    cardWidth,
    containerMinWidth,
    containerMinHeight,
    containerWidth,
    containerHeight
  } = getChartCardTableContainerSize<DataKey, DataRow>({
    view,
    cardSize,
    tableSize
  });
  const {
    showTopScrollFade,
    showBottomScrollFade,
    measuredHeaderHeight,
    measuredFooterHeight
  } = useTableBodyScrollDecoration({
    enabled: useBodyScrollDecoration === true,
    tableContainerRef,
    tableHeadRootRef,
    tableFooterRootRef,
    processedData,
    globalFilter,
    containerHeight,
    containerWidth,
    pageIndex: completePaginationState?.pageIndex,
    pageSize: completePaginationState?.pageSize
  });
  return <StyledCard minWidth={cardWidth}>
      <IconTemplates />
      <ToolbarRow isInteractionDisabled={isFetchLoading === true}>
        <Grid item container alignItems="center" gap="0px">
          <StyledTypographyNoWrap variant="h3">{title}</StyledTypographyNoWrap>
          {allowColumnsSelection === true && <MultiSelectDropdown key="select-columns" includeSearch title={t('selectColumns')} buttonTitle={t('columns')} options={columnsOptions} allowSelectAll value={selectedColumnsIds} onChange={onChange} fixedPositionOnOpen />}
          {useFuzzySearch === true && <>
              <Spacer width={theme.spacing(1)} />
              <SearchButton value={globalFilter} handleSubmit={handleSearchSubmit} isDebouncedSearch />
            </>}
        </Grid>
        <Grid display="flex" gap={theme.spacing(1)}>
          {buttons}
          {moreMenu}
        </Grid>
      </ToolbarRow>
      <TableScrollAreaShell width={containerWidth} height={containerHeight} minWidth={containerMinWidth} minHeight={containerMinHeight}>
        <StyledTableContainer ref={setTableContainerRef} useOverscrollContain={useOverscrollContain ?? false}>
          <Table<DataKey, DataRow> tableRef={tableRef} resetTableRef={resetTableRef} data={processedData} setCompletePaginationState={setCompletePaginationState} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} totalsAndRangeData={totalsAndRangeData} columns={columns} selections={selections} choicesList={choicesList} choicesMap={choicesMap} elementsDescriptionsMap={elementsDescriptionsMap} tableContainerRef={tableContainerRef} containerClientWidth={containerClientWidth} tableHeadRootRef={tableHeadRootRef} tableFooterRootRef={tableFooterRootRef} usePagination={usePagination} useFuzzySearch={useFuzzySearch ?? false} useDefaultSorting={useDefaultSorting ?? true} useDefaultPinning={useDefaultPinning ?? true} useColumnsDecorations={useColumnsDecorations ?? false} allowRowsSelection={allowRowsSelection ?? false} allowMultipleColumnsSorting={allowMultipleColumnsSorting ?? false} allowColumnFiltering={allowColumnFiltering ?? false} allowDateFiltering={allowDateFiltering ?? false} onOpenColumnFilter={onOpenColumnFilter} allowColumnResizing={allowColumnResizing ?? false} allowColumnAutoResizing={allowColumnAutoResizing ?? false} allowColumnPinning={allowColumnPinning ?? false} allowColumnReordering={allowColumnReordering ?? false} enableNullDimensionValue={enableNullDimensionValue ?? true} isServerFetch={isServerFetch} serverTotalRows={serverTotalRows} serverPagination={serverPagination} serverSorting={serverSorting} onServerPaginationChange={onServerPaginationChange} onServerSortingChange={onServerSortingChange} isFetchLoading={isFetchLoading} dispatch={dispatch} t={t} />
        </StyledTableContainer>
        {showTopScrollFade && measuredHeaderHeight > 0 ? <TopScrollFade $top={measuredHeaderHeight} $backgroundColor={defaults.table.backgroundColor.head} /> : null}
        {showBottomScrollFade && measuredFooterHeight > 0 ? <BottomScrollFade $bottom={measuredFooterHeight} $backgroundColor={defaults.table.backgroundColor.footer} /> : null}
      </TableScrollAreaShell>
    </StyledCard>;
};
export const ChartCardTableContainerSkeleton = t0 => {
  const $ = _c(7);
  const {
    view,
    cardSize,
    tableSize
  } = t0;
  let t1;
  if ($[0] !== cardSize || $[1] !== tableSize || $[2] !== view) {
    t1 = getChartCardTableContainerSize({
      view,
      cardSize,
      tableSize
    });
    $[0] = cardSize;
    $[1] = tableSize;
    $[2] = view;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    containerWidth,
    containerHeight
  } = t1;
  const addedWidth = CHART_CARD_CARD_LAYOUT.padding * 2;
  const addedHeight = CHART_CARD_CARD_LAYOUT.padding * 2 + CHART_CARD_TABLE_LAYOUT.height.title + CHART_CARD_CARD_LAYOUT.gap;
  const skeletonWidth = typeof containerWidth === "string" ? containerWidth : `calc(${containerWidth}px + ${addedWidth}px)`;
  const skeletonHeight = typeof containerHeight === "string" ? containerHeight : `calc(${containerHeight}px + ${addedHeight}px)`;
  let t2;
  if ($[4] !== skeletonHeight || $[5] !== skeletonWidth) {
    t2 = <Skeleton width={skeletonWidth} height={skeletonHeight} variant="rectangular" />;
    $[4] = skeletonHeight;
    $[5] = skeletonWidth;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const getButtons = <DataKey extends string = string, DataRow extends ChartDataRow<DataKey> = ChartDataRow<DataKey>>({
  tableRef,
  getContext,
  otherCardTypes,
  changeCardType,
  actions,
  expandedToggle
}: {
  tableRef: RefObject<ChartCardTable<DataKey, DataRow> | null>;
  getContext: () => ChartCardActionContext<DataKey, DataRow> | null;
  otherCardTypes: ChartCardType[];
  changeCardType: ChangeCardType;
  actions: ChartCardChartContainerProps<DataKey, DataRow>['actions'];
  expandedToggle: ChartCardChartContainerProps<DataKey, DataRow>['expandedToggle'];
}): ReactNode => {
  const buttons: ReactElement[] = [];
  if (expandedToggle != null) {
    const leftIcon = expandedToggle.expanded ? <MinimizeIcon /> : <MaximizeIcon />;
    buttons.push(<Button key="expandToggle" onClick={() => {
      expandedToggle.toggle();
    }} variant="text" leftIcon={leftIcon} />);
  }
  if (actions != null) {
    for (const {
      id,
      label,
      icon,
      onClick,
      Component
    } of actions) {
      if (Component == null) {
        buttons.push(<Button key={id} onClick={getActionClickHandler(id, onClick, tableRef.current)} variant="text" leftIcon={icon}>
            {icon == null ? label ?? id : label}
          </Button>);
      } else {
        buttons.push(<Component key={id} id={id} getContext={getContext} />);
      }
    }
  }
  if (otherCardTypes.length > 0) {
    buttons.push(<ChartTableSwap changeCardType={changeCardType} cardType="table" otherCardTypes={otherCardTypes} />);
  }
  return buttons;
};
const ToolbarRow = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isInteractionDisabled'
})<{
  isInteractionDisabled?: boolean;
}>(({
  theme,
  isInteractionDisabled
}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  width: '100%',
  alignItems: 'center',
  height: `${CHART_CARD_TABLE_LAYOUT.height.title}px`,
  ...(isInteractionDisabled === true && {
    pointerEvents: 'none'
  })
}));
const TableScrollAreaShell = styled('div', {
  shouldForwardProp: prop => prop !== 'width' && prop !== 'height' && prop !== 'minWidth' && prop !== 'minHeight'
})<{
  width: number | `${number}%`;
  height: number | `${number}%`;
  minWidth: number;
  minHeight: number;
}>(({
  theme,
  width,
  height,
  minWidth,
  minHeight
}) => ({
  position: 'relative',
  overflow: 'hidden',
  width,
  height,
  minWidth,
  minHeight,
  borderRadius: theme.borderRadius.medium
}));
const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: prop => prop !== 'useOverscrollContain'
})<{
  useOverscrollContain: boolean;
}>(({
  useOverscrollContain
}) => ({
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflow: 'auto',
  containerType: 'inline-size',
  overscrollBehavior: useOverscrollContain ? 'contain' : 'auto'
}));
const TopScrollFade = styled('div', {
  shouldForwardProp: prop => prop !== '$top' && prop !== '$backgroundColor'
})<{
  $top: number;
  $backgroundColor?: string;
}>(({
  theme,
  $top,
  $backgroundColor
}) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: $top,
  height: 48,
  pointerEvents: 'none',
  zIndex: 1,
  background: `linear-gradient(to bottom, ${$backgroundColor ?? 'transparent'}, transparent)`,
  borderTopLeftRadius: theme.borderRadius.medium,
  borderTopRightRadius: theme.borderRadius.medium
}));
const BottomScrollFade = styled('div', {
  shouldForwardProp: prop => prop !== '$bottom' && prop !== '$backgroundColor'
})<{
  $bottom: number;
  $backgroundColor?: string;
}>(({
  theme,
  $bottom,
  $backgroundColor
}) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: $bottom,
  height: 48,
  pointerEvents: 'none',
  zIndex: 1,
  background: `linear-gradient(to bottom, transparent, ${$backgroundColor ?? 'transparent'})`,
  borderBottomLeftRadius: theme.borderRadius.medium,
  borderBottomRightRadius: theme.borderRadius.medium
}));
export default ChartCardTableContainer;